import { useCallback, useEffect, useRef, useState } from 'react'
import type { ControllerProfile } from '../lib/pairing'
import { makeCommandEnvelope, REMOTE_PROTOCOL_VERSION, type RemoteCommand, type RemoteTvState } from '../lib/remoteProtocol'
import { RemoteSocket, type RemoteSocketStatus } from '../lib/remoteSocket'

export interface ControllerConnectionState {
  status: RemoteSocketStatus
  tvOnline: boolean
  controllers: number
  tvState: RemoteTvState | null
  lastAck: { id: string; status: 'delivered' | 'queued' | 'rejected'; message?: string } | null
}

export type ControllerAck = NonNullable<ControllerConnectionState['lastAck']>

interface PendingAck {
  resolve: (ack: ControllerAck) => void
  reject: (error: Error) => void
  timer: number
}

const EMPTY: ControllerConnectionState = {
  status: 'idle', tvOnline: false, controllers: 0, tvState: null, lastAck: null,
}

export function useControllerRemote(profile: ControllerProfile | null) {
  const [state, setState] = useState<ControllerConnectionState>(EMPTY)
  const socketRef = useRef<RemoteSocket | null>(null)
  const pendingAcks = useRef(new Map<string, PendingAck>())

  useEffect(() => {
    setState(EMPTY)
    if (!profile) return
    // Captured for the cleanup rather than read from the ref there: the ref is
    // stable today, but reading it at teardown is the pattern that breaks when
    // it stops being.
    const acks = pendingAcks.current
    const socket = new RemoteSocket({
      deviceId: profile.deviceId,
      role: 'controller',
      token: profile.controllerToken,
      onStatus: (status) => setState((value) => ({ ...value, status })),
      onMessage: (message) => {
        if (message.type === 'hello' || message.type === 'presence') {
          setState((value) => ({ ...value, tvOnline: message.tvOnline, controllers: message.controllers }))
        } else if (message.type === 'state') {
          setState((value) => ({ ...value, tvState: message.state, tvOnline: true }))
        } else if (message.type === 'ack') {
          const ack = { id: message.id, status: message.status, message: message.message }
          setState((value) => ({ ...value, lastAck: ack }))
          const pending = pendingAcks.current.get(message.id)
          if (pending) {
            window.clearTimeout(pending.timer)
            pendingAcks.current.delete(message.id)
            pending.resolve(ack)
          }
        }
      },
    })
    socketRef.current = socket
    socket.start()
    return () => {
      if (socketRef.current === socket) socketRef.current = null
      socket.stop()
      acks.forEach((pending) => {
        window.clearTimeout(pending.timer)
        pending.reject(new Error('Controller connection changed before the TV acknowledged the command.'))
      })
      acks.clear()
    }
  }, [profile])

  const send = useCallback((command: RemoteCommand) => {
    const envelope = makeCommandEnvelope(command)
    socketRef.current?.send(envelope)
    return envelope.id
  }, [])

  const requestState = useCallback(() => {
    socketRef.current?.send({ v: REMOTE_PROTOCOL_VERSION, type: 'state_request' }, 0)
  }, [])

  const sendAndWait = useCallback((command: RemoteCommand, timeoutMilliseconds = 8_000) => {
    const socket = socketRef.current
    if (!socket) return Promise.reject(new Error('Controller is not connected.'))
    const envelope = makeCommandEnvelope(command)
    return new Promise<ControllerAck>((resolve, reject) => {
      const timer = window.setTimeout(() => {
        pendingAcks.current.delete(envelope.id)
        reject(new Error('TV relay acknowledgement timed out.'))
      }, Math.max(1_000, Math.min(30_000, timeoutMilliseconds)))
      pendingAcks.current.set(envelope.id, { resolve, reject, timer })
      // Commands carrying private sync data must never sit in the socket's
      // reconnect queue. The caller already requires a live TV.
      if (!socket.send(envelope, 0)) {
        window.clearTimeout(timer)
        pendingAcks.current.delete(envelope.id)
        reject(new Error('Controller link is not ready. Reconnect and try again.'))
      }
    })
  }, [])

  return { ...state, send, sendAndWait, requestState }
}
